import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, Sky, Text, Cloud } from "@react-three/drei";
import { useRef, useState, useEffect } from "react";
import { get3DSimData } from "../api";

function SolarPanel({ position, efficiency, power }) {
  const meshRef = useRef();
  const glowRef = useRef();
  const intensity = Math.min((efficiency || 0.2) / 0.5, 1);

  useFrame((state) => {
    if (meshRef.current)
      meshRef.current.rotation.x = -0.3 + Math.sin(state.clock.elapsedTime * 0.2) * 0.015;
    if (glowRef.current)
      glowRef.current.intensity = intensity * 0.8 + Math.sin(state.clock.elapsedTime * 2) * 0.2;
  });

  // Panel color: dark blue/teal like real solar panels
  const panelColor = `hsl(${210 + intensity * 15}, ${40 + intensity * 20}%, ${18 + intensity * 8}%)`;

  return (
    <group position={position}>
      {/* Panel surface */}
      <mesh ref={meshRef} castShadow receiveShadow>
        <boxGeometry args={[2.6, 0.07, 1.6]} />
        <meshStandardMaterial color={panelColor} metalness={0.5} roughness={0.2} />
      </mesh>
      {/* Cell grid lines on panel */}
      <mesh position={[0, 0.04, 0]}>
        <boxGeometry args={[2.58, 0.01, 1.58]} />
        <meshStandardMaterial color="#1a3a5c" wireframe />
      </mesh>
      {/* Support pole */}
      <mesh position={[0, -0.38, 0]}>
        <cylinderGeometry args={[0.035, 0.035, 0.68, 8]} />
        <meshStandardMaterial color="#9ca3af" metalness={0.9} roughness={0.2} />
      </mesh>
      {/* Base foot */}
      <mesh position={[0, -0.72, 0]}>
        <boxGeometry args={[0.5, 0.06, 0.5]} />
        <meshStandardMaterial color="#6b7280" metalness={0.7} />
      </mesh>
      {/* Subtle glow under panel when active */}
      <pointLight ref={glowRef} position={[0, 0.4, 0]} color="#fbbf24" distance={3} intensity={intensity * 0.6} />
    </group>
  );
}

function PanelArray({ origin, label, data }) {
  const cols = 5, rows = 4;
  return (
    <group position={origin}>
      <Text
        position={[cols * 1.8, 2.8, rows * 1.3]}
        fontSize={0.42}
        color="#374151"
        anchorX="center"
        font={undefined}
      >
        {label}
      </Text>
      {Array.from({ length: cols }).map((_, ci) =>
        Array.from({ length: rows }).map((_, ri) => {
          const idx = ci * rows + ri;
          return (
            <SolarPanel
              key={`${ci}-${ri}`}
              position={[ci * 3.6, 0.5, ri * 2.6]}
              efficiency={data?.efficiency?.[idx % (data?.efficiency?.length || 1)] ?? 0.2}
              power={data?.power_kw?.[idx % (data?.power_kw?.length || 1)] ?? 300}
            />
          );
        })
      )}
    </group>
  );
}

function PowerCable({ from, to }) {
  const ref = useRef();
  useFrame((s) => {
    if (ref.current)
      ref.current.material.opacity = 0.6 + Math.sin(s.clock.elapsedTime * 4) * 0.2;
  });
  return (
    <mesh ref={ref} position={[(from[0]+to[0])/2, 0.12, (from[2]+to[2])/2]}>
      <boxGeometry args={[Math.abs(to[0]-from[0])||0.05, 0.04, Math.abs(to[2]-from[2])||0.05]} />
      <meshStandardMaterial color="#f97316" transparent opacity={0.7} />
    </mesh>
  );
}

function Inverter({ position, active }) {
  const lightRef = useRef();
  useFrame((s) => {
    if (lightRef.current)
      lightRef.current.intensity = active ? 0.8 + Math.sin(s.clock.elapsedTime*3)*0.3 : 0.2;
  });
  return (
    <group position={position}>
      <mesh castShadow>
        <boxGeometry args={[1.4, 1.1, 0.6]} />
        <meshStandardMaterial color="#e5e7eb" metalness={0.4} roughness={0.5} />
      </mesh>
      <mesh position={[0, 0.28, 0.31]}>
        <boxGeometry args={[0.9, 0.45, 0.02]} />
        <meshStandardMaterial color="#1e40af" />
      </mesh>
      <pointLight ref={lightRef} position={[0, 1.1, 0]} color="#22c55e" distance={5} />
      <Text position={[0, 0.82, 0.32]} fontSize={0.19} color="#15803d" anchorX="center">
        INVERTER
      </Text>
    </group>
  );
}

// Sandy desert ground
function Ground() {
  return (
    <>
      <mesh rotation={[-Math.PI/2, 0, 0]} position={[0, -0.03, 0]} receiveShadow>
        <planeGeometry args={[120, 120]} />
        <meshStandardMaterial color="#d4b896" roughness={1} />
      </mesh>
      {/* Subtle sand texture patches */}
      {[[-20,5],[15,-10],[0,20],[-5,-18],[25,8]].map(([x,z],i)=>(
        <mesh key={i} rotation={[-Math.PI/2,0,Math.random()]} position={[x,-0.02,z]}>
          <circleGeometry args={[3+Math.random()*4, 8]} />
          <meshStandardMaterial color="#c9a87a" roughness={1} />
        </mesh>
      ))}
    </>
  );
}

// Animated sun disc
function Sun() {
  return (
    <mesh position={[40, 35, -60]}>
      <sphereGeometry args={[3, 16, 16]} />
      <meshStandardMaterial color="#fef08a" emissive="#fbbf24" emissiveIntensity={2} />
    </mesh>
  );
}

export default function SolarTwin3D() {
  const [simData, setSimData] = useState(null);
  const [stats,   setStats  ] = useState({ avg_power:0, avg_eff:0, avg_irr:0 });

  useEffect(() => {
    get3DSimData(48).then(r => {
      setSimData(r.data);
      const c = r.data.combined;
      setStats({ avg_power:c.avg_power, avg_eff:c.avg_eff, avg_irr:c.avg_irr });
    }).catch(console.error);
  }, []);

  const dg    = simData?.["Desert Gardens"];
  const sails = simData?.["Sails"];

  return (
    <div style={{ position:"relative", width:"100%", height:"520px", borderRadius:"10px", overflow:"hidden", background:"#e0f2fe" }}>
      <Canvas
        shadows
        camera={{ position:[18,12,22], fov:52 }}
        gl={{ antialias:true }}
      >
        {/* Daytime Sky */}
        <Sky
          distance={450000}
          sunPosition={[40, 35, -60]}
          inclination={0.49}
          azimuth={0.25}
          turbidity={4}
          rayleigh={0.5}
        />

        {/* Lighting — bright sunny day */}
        <ambientLight intensity={1.2} color="#fff8f0" />
        <directionalLight
          position={[40, 35, -60]}
          intensity={3.5}
          castShadow
          shadow-mapSize={[2048, 2048]}
          color="#fff5e0"
          shadow-camera-far={200}
          shadow-camera-left={-60}
          shadow-camera-right={60}
          shadow-camera-top={60}
          shadow-camera-bottom={-60}
        />
        <hemisphereLight skyColor="#87ceeb" groundColor="#d4b896" intensity={0.8} />

        {/* Clouds */}
        <Cloud position={[-30, 25, -50]} speed={0.1} opacity={0.6} />
        <Cloud position={[20,  28, -70]} speed={0.08} opacity={0.4} />
        <Cloud position={[50,  22, -40]} speed={0.12} opacity={0.5} />

        <Sun />
        <Ground />

        {/* Panel arrays */}
        <PanelArray origin={[-18, 0, -8]} label="DESERT GARDENS" data={dg} />
        <PanelArray origin={[4,   0, -8]} label="SAILS"          data={sails || dg} />

        {/* Inverters */}
        <Inverter position={[-9, 0.55,  6]} active={stats.avg_power > 200} />
        <Inverter position={[13, 0.55,  6]} active={stats.avg_power > 200} />

        {/* Power cables */}
        <PowerCable from={[-9,0,6]} to={[13,0,6]} />
        <PowerCable from={[2, 0,6]} to={[2, 0,12]} />

        {/* Grid connection */}
        <mesh position={[2, 0.6, 12]} castShadow>
          <boxGeometry args={[2, 1.2, 0.8]} />
          <meshStandardMaterial color="#d1d5db" metalness={0.5} roughness={0.4} />
        </mesh>
        <Text position={[2, 1.5, 12]} fontSize={0.26} color="#374151" anchorX="center">
          GRID CONNECTION
        </Text>

        <OrbitControls enablePan maxPolarAngle={Math.PI/2.05} minDistance={6} maxDistance={55} />
      </Canvas>

      {/* HUD — light background */}
      <div style={{ position:"absolute", top:14, left:14, display:"flex", flexDirection:"column", gap:6 }}>
        {[
          { label:"AVG POWER",  value:`${stats.avg_power.toFixed(1)} kW`,    color:"#d97706", bg:"#fffbeb", border:"#fde68a" },
          { label:"EFFICIENCY", value:`${(stats.avg_eff*100).toFixed(1)}%`,  color:"#15803d", bg:"#f0fdf4", border:"#bbf7d0" },
          { label:"IRRADIANCE", value:`${stats.avg_irr.toFixed(0)} W/m²`,    color:"#1d4ed8", bg:"#eff6ff", border:"#bfdbfe" },
        ].map(s => (
          <div key={s.label} style={{ background:s.bg, border:`1px solid ${s.border}`, padding:"6px 12px", borderRadius:7, fontFamily:"Inter,sans-serif", fontSize:11, color:"#374151", display:"flex", gap:8, alignItems:"center", boxShadow:"0 1px 4px rgba(0,0,0,0.08)" }}>
            <span style={{ fontWeight:500, color:"#94a3b8", fontSize:9, textTransform:"uppercase", letterSpacing:"0.05em" }}>{s.label}</span>
            <b style={{ color:s.color }}>{s.value}</b>
          </div>
        ))}
      </div>
      <div style={{ position:"absolute", bottom:12, right:14, fontFamily:"Inter,sans-serif", fontSize:10, color:"#94a3b8", background:"rgba(255,255,255,0.7)", padding:"4px 10px", borderRadius:5 }}>
        Drag · Scroll · Right-click pan
      </div>
    </div>
  );
}